import { FC, useEffect, useState } from "react";
import { IOrderProduct } from "../types";
import styles from "./OrderQuantityChange.module.css";
import { Modal } from "antd";
import { changeOrderProductReturnedQuantity } from "../api";
import { LMInputNumberIncremental } from "../../../components/LMInputNumberIncremental";
import { useTranslation } from "react-i18next";
import { LMNewButton } from "../../../components/LMNewButton";
import { Body1 } from "../../../components/Typography";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../app/store";
import { refreshOrder } from "../slice";
import { useAppNotifications, getErrorMessage } from "../../../components/LMNotifications";

interface Props {
  selectedProduct: IOrderProduct | null;
  orderId: string;
  onClose: () => void;
}

export const OrderQuantityChange: FC<Props> = ({
  selectedProduct,
  onClose,
  orderId,
}) => {
  const [returnedQuantity, setReturnedQuantity] = useState(
    selectedProduct?.returnedQuantity || 0
  );
  const [showMaxWarning, setShowMaxWarning] = useState(false);
  const { t } = useTranslation("order");
  const dispatch = useDispatch<AppDispatch>();
  const { contextHolder, success, error } = useAppNotifications();

  useEffect(() => {
    if (!selectedProduct) return;
    setReturnedQuantity(selectedProduct.returnedQuantity);
    setShowMaxWarning(false);
  }, [selectedProduct]);

  const handleSubmit = async () => {
    if (!selectedProduct) return;
    try {
      await changeOrderProductReturnedQuantity(
        orderId,
        selectedProduct.productId,
        returnedQuantity
      );
      dispatch(refreshOrder({ orderId }));
      success(t("successQuantityMessage"));
      onClose();
    } catch (e) {
      error(t("errorQuantityMessage"), getErrorMessage(e));
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        open={!!selectedProduct}
        title={t("changeReturnedQuantity")}
        footer={null}
        onCancel={onClose}
        width={400}
        className={styles.container}
      >
        <div className={styles.content}>
          <LMInputNumberIncremental
            name="list"
            value={returnedQuantity}
            max={selectedProduct?.collectedQuantity}
            onValueChange={(value: number) => {
              if (value < returnedQuantity) {
                setShowMaxWarning(false);
              }
              setReturnedQuantity(value);
            }}
            onMaxExceeded={() => setShowMaxWarning(true)}
          />
          <LMNewButton
            disabled={returnedQuantity === selectedProduct?.returnedQuantity}
            onClick={handleSubmit}
          >
            {t("update")}
          </LMNewButton>
        </div>
        {showMaxWarning && (
          <Body1 className={styles.warning}>
            {t("returnedQuantityWarning")}
          </Body1>
        )}
      </Modal>
    </>
  );
};
